import React, { useEffect, useState, useCallback } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api, { fileUrl, formatApiError } from "../lib/api";
import { Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { AFRICAN_COUNTRIES, POSITIONS } from "../lib/constants";
import { ShieldCheck, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

const ANY = "__any__";
const VERIFIED_LS_KEY = "afri_browse_verified_only";

export default function PlayersBrowse() {
  const { t } = useTranslation();
  const [players, setPlayers] = useState([]);
  const [country, setCountry] = useState("");
  const [position, setPosition] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(() => localStorage.getItem(VERIFIED_LS_KEY) === "1");

  const load = useCallback(async () => {
    try {
      const params = {};
      if (country) params.country = country;
      if (position) params.position = position;
      if (verifiedOnly) params.verified_only = true;
      const { data } = await api.get("/players", { params });
      setPlayers(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [country, position, verifiedOnly]);

  useEffect(() => { load(); }, [load]);

  const inputCls = "bg-transparent border-white/20 rounded-sm h-11 focus:border-[#e65f2b]";

  return (
    <div className="min-h-screen bg-black text-white grain">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b]">{t("nav.players")}</div>
        <h1 className="mt-2 font-display text-4xl font-bold uppercase tracking-tight">{t("players.title")}</h1>

        <div className="mt-6 border border-white/10 bg-[#141414] p-4 flex flex-wrap gap-3 items-end">
          <div className="min-w-56">
            <Select value={country || ANY} onValueChange={(v) => setCountry(v === ANY ? "" : v)}>
              <SelectTrigger data-testid="browse-country" className={inputCls}><SelectValue placeholder={t("players.country")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t("players.country")}</SelectItem>
                {AFRICAN_COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-56">
            <Select value={position || ANY} onValueChange={(v) => setPosition(v === ANY ? "" : v)}>
              <SelectTrigger data-testid="browse-position" className={inputCls}><SelectValue placeholder={t("players.position")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t("players.position")}</SelectItem>
                {POSITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 border border-white/15 h-11 px-3">
            <ShieldCheck size={14} className={verifiedOnly ? "text-[#eab308]" : "text-white/40"} />
            <span className={`text-xs uppercase tracking-widest ${verifiedOnly ? "text-[#eab308]" : "text-white/60"}`}>{t("players.verifiedOnly")}</span>
            <Switch
              data-testid="browse-verified-toggle"
              checked={verifiedOnly}
              onCheckedChange={(v) => {
                setVerifiedOnly(v);
                localStorage.setItem(VERIFIED_LS_KEY, v ? "1" : "0");
              }}
              className="data-[state=checked]:bg-[#eab308]"
            />
          </div>
          <Button onClick={load} className="bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold uppercase tracking-widest rounded-sm h-11" data-testid="browse-search-btn">            <Search size={14} className="mr-2" />{t("common.search")}
          </Button>
        </div>

        <div className="mt-6 grid md:grid-cols-3 lg:grid-cols-4 gap-4">
          {players.map((p) => (
            <Link key={p.id || p.user_id} to={`/players/${p.user_id}`} data-testid={`browse-card-${p.user_id}`}>
              <div className="border border-white/10 bg-[#141414] hover:border-[#e65f2b] transition-colors">
                <div className="aspect-[4/5] bg-black relative overflow-hidden">
                  {p.photo_path
                    ? <img src={fileUrl(p.photo_path)} alt={p.name} className="w-full h-full object-cover" />
                    : <div className="w-full h-full bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center font-display text-6xl text-white/20">{(p.name || "?")[0]}</div>}
                  {p.verified && (
                    <div className="absolute top-3 right-3 bg-[#eab308] text-black px-2 py-1 text-xs font-bold uppercase tracking-widest flex items-center gap-1">
                      <ShieldCheck size={12} />{t("common.verified")}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="font-display text-lg font-bold uppercase truncate flex items-center gap-1.5">
                    <span className="truncate">{p.name}</span>
                    {p.verified && <ShieldCheck size={14} className="text-[#eab308] shrink-0" data-testid="card-verified-badge" />}
                  </div>
                  <div className="text-xs text-white/60 mt-1">{p.country || "—"}</div>
                  <div className="text-xs text-white/70 uppercase tracking-widest mt-2">{p.position || "—"} · {p.age ? `${p.age}y` : "—"}</div>
                </div>
              </div>
            </Link>
          ))}
          {players.length === 0 && <div className="text-white/50 md:col-span-4">{t("players.empty")}</div>}
        </div>
      </div>
      <Footer />
    </div>
  );
}
